import { useState } from 'react'
import { Plus } from 'lucide-react'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { SearchSelect } from '@/components/search-select'
import type { QuoteLine } from '../api/types'
import type { QuoteOnlyProduct } from '../catalog/queries'
import { EditorDrawer } from './drawer'

/**
 * A catalog product priced on each quote (a Safeware plan; USA-1109): Sales chooses the equipment it
 * covers and enters its Net and Sell. It joins the quote as that equipment's warranty.
 */
export function PricedPlanDrawer({
  product,
  equipment,
  busy,
  onCancel,
  onAdd,
}: {
  product: QuoteOnlyProduct
  equipment: QuoteLine[]
  busy: boolean
  onCancel: () => void
  onAdd: (values: { parent: QuoteLine; net: string; sell: string }) => void
}) {
  const [parentId, setParentId] = useState<string | undefined>(
    equipment.length === 1 ? equipment[0]?.id : undefined,
  )
  const [net, setNet] = useState('')
  const [sell, setSell] = useState('')
  const parent = equipment.find((line) => line.id === parentId)
  const valid = Boolean(parent) && net.trim() !== '' && Number(net) >= 0 && Number(sell) > 0

  return (
    <EditorDrawer
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
      open
      title={`Add ${product.model}`}
    >
      <form
        className="grid content-start gap-5 overflow-y-auto p-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (parent && valid) onAdd({ parent, net, sell })
        }}
      >
        <div className="rounded-md bg-muted/50 px-3 py-3 text-sm">
          <p className="font-medium">
            {product.manufacturer} {product.model}
          </p>
          <p className="mt-1 text-muted-foreground">{product.title}</p>
        </div>
        {equipment.length ? (
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground" htmlFor="priced-plan-covers">
              Covers
            </Label>
            <SearchSelect
              id="priced-plan-covers"
              className="h-9 w-full"
              value={parentId}
              options={equipment.map((line) => ({
                value: line.id,
                label: [line.manufacturer, line.model || line.description].filter(Boolean).join(' '),
              }))}
              onChange={(next) => next && setParentId(next)}
              placeholder="Choose equipment"
              searchPlaceholder="Search equipment"
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Add the equipment this plan covers first.</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground" htmlFor="priced-plan-net">
              Net
            </Label>
            <InlineFieldInput
              id="priced-plan-net"
              inputMode="decimal"
              onChange={(event) => setNet(event.target.value)}
              value={net}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground" htmlFor="priced-plan-sell">
              Sell
            </Label>
            <InlineFieldInput
              id="priced-plan-sell"
              inputMode="decimal"
              onChange={(event) => setSell(event.target.value)}
              value={sell}
            />
          </div>
        </div>
        <LoadingButton className="ml-auto" disabled={!valid} loading={busy} loadingText="Adding..." type="submit">
          <Plus />
          Add plan
        </LoadingButton>
      </form>
    </EditorDrawer>
  )
}
